'use client';

import { Input, Label } from '@/components/elements';
import { ErrorLabel } from '@/components/elements/ErrorLabel';
import { useAuthModal } from '@/lib/context/AuthModalContext';
import { useRouter } from 'next/navigation';
import { ChangeEvent, FormEvent, useMemo, useState } from 'react';
import paths from '@/paths';
import { register } from '@/lib/auth';
import { signIn } from 'next-auth/react'; // Add this

interface IInputFields {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  passwordConfirm: string;
  isAgency: boolean;
}

const initialInputs = { firstName: '', lastName: '', email: '', password: '', passwordConfirm: '', isAgency: false };
const initialErrors = {
  firstName: false,
  lastName: false,
  email: false,
  password: false,
  passwordConfirm: false,
  isAgency: false,
};

interface IRegisterFormProps {
  handleClose: () => void;
}

export const RegisterForm = ({ handleClose }: IRegisterFormProps) => {
  const router = useRouter();
  const { userType } = useAuthModal();
  const [errors, setErrors] = useState<Record<keyof IInputFields, boolean>>(initialErrors);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputs, setInputs] = useState<IInputFields>(() => ({
    ...initialInputs,
    isAgency: userType === 'provider',
  }));

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputs((prevValue) => ({ ...prevValue, [e.target.name]: e.target.value }));
    setErrors((prevValue) => ({ ...prevValue, [e.target.name]: false }));
    setErrorMessage(null);
    if (e.target.name === 'password' && inputs.passwordConfirm !== '')
      checkMatch(inputs.passwordConfirm, e.target.value, 'passwordConfirm');
    if (e.target.name === 'passwordConfirm' && inputs.passwordConfirm !== '')
      checkMatch(e.target.value, inputs.password, 'passwordConfirm');
  };

  const checkMatch = (firstValue: string, secondValue: string, key: string) => {
    setErrors((prevValue) => ({ ...prevValue, [key]: firstValue !== secondValue }));
  };

  const isDisabled = useMemo(
    () =>
      !inputs.firstName.length ||
      !inputs.lastName.length ||
      !inputs.email.length ||
      !inputs.password.length ||
      !inputs.passwordConfirm.length ||
      inputs.password !== inputs.passwordConfirm,
    [inputs]
  );

  const validateForm = () => {
    let hasErrors = false;
    const newErrors = { ...initialErrors };

    if (!inputs.firstName.trim()) {
      newErrors.firstName = true;
      hasErrors = true;
    }

    if (!inputs.lastName.trim()) {
      newErrors.lastName = true;
      hasErrors = true;
    }

    if (!inputs.email || !inputs.email.includes('@')) {
      newErrors.email = true;
      hasErrors = true;
    }

    if (!inputs.password || inputs.password.length < 6) {
      newErrors.password = true;
      hasErrors = true;
    }

    if (inputs.password !== inputs.passwordConfirm) {
      newErrors.passwordConfirm = true;
      hasErrors = true;
    }

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    const { passwordConfirm, ...payload } = inputs;

    try {
      const result = await register(payload);

      if ('success' in result && !result.success) {
        const errorMsg = result.message || 'Registration failed. Please try again.';

        setErrorMessage(errorMsg);
        if (errorMsg.toLowerCase().includes('password')) setErrors((prev) => ({ ...prev, password: true }));
        if (errorMsg.toLowerCase().includes('first name')) setErrors((prev) => ({ ...prev, firstName: true }));
        if (errorMsg.toLowerCase().includes('last name')) setErrors((prev) => ({ ...prev, lastName: true }));
        if (errorMsg.toLowerCase().includes('email')) setErrors((prev) => ({ ...prev, email: true }));
        return;
      }

      // Step 2: Automatically sign in after successful registration
      const signInResult = await signIn("credentials", {
        email: inputs.email,
        password: inputs.password,
        redirect: false,
      });

      if (signInResult?.error) {
        // Registration succeeded but auto-login failed, not critical
        // User can log in manually
        console.error('Auto-login after registration failed:', signInResult.error);
        setErrorMessage('Account created! Please sign in.');
        return;
      }

      // Step 3: Close modal and redirect
      handleClose();
      if (userType === 'provider') router.push(paths.registerTourOperater());
      router.refresh();

    } catch (error: any) {
      console.error('Registration error:', error);
      const errorMsg = error.message || 'Registration failed. Please try again.';

      setErrorMessage(errorMsg);
      if (errorMsg.toLowerCase().includes('password')) setErrors((prev) => ({ ...prev, password: true }));
      if (errorMsg.toLowerCase().includes('first name')) setErrors((prev) => ({ ...prev, firstName: true }));
      if (errorMsg.toLowerCase().includes('last name')) setErrors((prev) => ({ ...prev, lastName: true }));
      if (errorMsg.toLowerCase().includes('email')) setErrors((prev) => ({ ...prev, email: true }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="login-modal__body flex flex-col gap-6 px-6 pt-6 pb-8">
        <img src="/static/images/logo.png" alt="FaunaExplore logo" height={42} width={37} />
        <h3 className="text-xl font-semibold font-rokkitt">Create Account</h3>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{errorMessage}</div>
        )}

        <div className="flex flex-col gap-8">
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="flex flex-row gap-2 w-full">
              <div className="flex flex-col flex-wrap gap-1 flex-grow min-w-0">
                <Label>First Name</Label>
                <Input.Controlled
                  name="firstName"
                  value={inputs.firstName}
                  handleChange={handleChange}
                  placeholder="Type here..."
                  isInvalid={errors.firstName}
                  className="w-full"
                />
                {errors.firstName && <ErrorLabel>First name is required</ErrorLabel>}
              </div>
              <div className="flex flex-col flex-wrap gap-1 flex-grow min-w-0">
                <Label>Last Name</Label>
                <Input.Controlled
                  name="lastName"
                  value={inputs.lastName}
                  handleChange={handleChange}
                  placeholder="Type here..."
                  isInvalid={errors.lastName}
                  className="w-full"
                />
                {errors.lastName && (
                  <ErrorLabel>
                    {errorMessage?.toLowerCase().includes('last name') ? 'Invalid last name.' : 'Required last name.'}
                  </ErrorLabel>
                )}
              </div>
            </div>

            <div className="flex flex-col flex-wrap gap-1">
              <Label>Email address</Label>
              <Input.Controlled
                name="email"
                value={inputs.email}
                handleChange={handleChange}
                placeholder="Type here..."
                isInvalid={errors.email}
              />
              {errors.email && <ErrorLabel>Please enter a valid email address</ErrorLabel>}
            </div>

            <div className="flex flex-col flex-wrap gap-1">
              <Label>Password</Label>
              <Input.Password
                name="password"
                value={inputs.password}
                handleChange={handleChange}
                placeholder="Type here..."
                isInvalid={errors.password}
              />
              {errors.password && <ErrorLabel>Password must be at least 6 characters</ErrorLabel>}
            </div>

            <div className="flex flex-col flex-wrap gap-1">
              <Label>Confirm Password</Label>
              <Input.Password
                name="passwordConfirm"
                value={inputs.passwordConfirm}
                handleChange={handleChange}
                placeholder="Type here..."
                isInvalid={errors.passwordConfirm}
              />
              {errors.passwordConfirm && <ErrorLabel>Passwords do not match</ErrorLabel>}
            </div>
          </form>
          <button
            className={`btn w-full ${isDisabled ? 'disabled' : ''}`}
            onClick={handleSubmit}
            disabled={isLoading || isDisabled}
          >
            {isLoading ? 'Creating account...' : 'Create account'}
          </button>
        </div>
      </div>
    </>
  );
};